import Ajv, { type ErrorObject, type ValidateFunction } from "ajv";
import { expandRefs, ID_PATTERN, isObject, REF_KEYWORD, type ContentRef, type JsonSchema } from "./schema.js";

// The versioned content registry (decision 0061: "one versioned registry of content tables with stable IDs ... JSON
// files checked against schemas ... schema and cross-reference checks at build time (a broken reference fails CI)
// and at load; at runtime bad rows are logged and skipped").
//
// One code path serves both: `loadContent` validates every table and row against its schema, collects the
// two-field references the schemas mark, drops bad rows (and, until nothing changes, rows whose reference points at
// a dropped or missing row), logs each issue once, and returns the registry with the issue list. The build-time
// check (`validateContent`, the `tools` CLI) runs the same load and fails when the list is not empty.

/** One table as its JSON file holds it. */
export interface TableFile<Row = unknown> {
  /** The table's stable name; references use it. */
  table: string;
  /** The table's version, a whole number from 1, bumped when its rows change meaning. */
  version: number;
  /** The rows. Each has a stable string `id`, unique in the table. */
  rows: Row[];
}

/** What went wrong, and where. Every issue is an error: a build with any fails; a load skips what it names. */
export interface ContentIssue {
  kind: "bad-table" | "missing-schema" | "bad-schema" | "schema" | "duplicate-id" | "broken-ref" | "row-check";
  table: string;
  /** The row's id, when the issue is a row's. */
  id?: string;
  /** JSON pointer inside the row, when known. */
  path?: string;
  message: string;
}

/** A table's load summary. */
export interface TableInfo {
  table: string;
  version: number;
  rows: number;
  skipped: number;
}

/** A row-level check beyond the schema (e.g. the strings table's message syntax). Return a message to reject. */
export type RowCheck = (row: { id: string } & Record<string, unknown>) => string | null;

/** What `loadContent` reads. */
export interface ContentInput {
  tables: readonly TableFile[];
  /** Each table's row schema, by table name. The `x-ref` shorthand is expanded here. */
  schemas: Readonly<Record<string, JsonSchema>>;
  /** Extra checks per table name. */
  rowChecks?: Readonly<Record<string, RowCheck>>;
  /** Where each issue is logged as it is found. Default `console.error` (the harness records it). Pass `null` to stay quiet. */
  log?: ((line: string, issue: ContentIssue) => void) | null;
}

/** The loaded content: only rows that passed every check. `T` maps table names to row types (the generated map). */
export interface ContentRegistry<T extends object = Record<string, { id: string }>> {
  /** Every issue the load found (empty when the content is clean). */
  readonly issues: readonly ContentIssue[];
  /** The tables loaded, with their versions and how many rows were kept and skipped. */
  tables(): TableInfo[];
  /** A table's version, or undefined if it isn't loaded. */
  version(table: string): number | undefined;
  /** Does the table have this row? */
  has(table: string, id: string): boolean;
  /** A row by table and id. */
  get<K extends keyof T & string>(table: K, id: string): T[K] | undefined;
  /** Every kept row of a table, in file order. */
  rows<K extends keyof T & string>(table: K): readonly T[K][];
  /** Follow a two-field reference. */
  resolve<K extends keyof T & string>(ref: ContentRef<K>): T[K] | undefined;
}

interface FoundRef {
  table: string;
  id: string;
  path: string;
}

/** Ajv with the reference keyword registered. The keyword's validate collects each reference it passes. */
function makeAjv(sink: { refs: FoundRef[] }): Ajv {
  const ajv = new Ajv({ allErrors: true, strict: true, allowUnionTypes: true });
  ajv.addKeyword({
    keyword: REF_KEYWORD,
    schemaType: "string",
    validate: (target: string, data: unknown, _parent?: unknown, cxt?: { instancePath: string }) => {
      if (isObject(data) && typeof data.id === "string") sink.refs.push({ table: target, id: data.id, path: cxt?.instancePath ?? "" });
      return true;
    },
  });
  return ajv;
}

function formatErrors(errors: readonly ErrorObject[] | null | undefined): string {
  return (errors ?? []).map((e) => `${e.instancePath || "/"} ${e.message ?? e.keyword}${e.keyword === "additionalProperties" ? ` (${String((e.params as { additionalProperty?: string }).additionalProperty)})` : ""}`).join("; ");
}

/** Load and check content. Never throws on bad content: bad rows are logged, skipped and listed in `issues`. */
export function loadContent<T extends object = Record<string, { id: string }>>(input: ContentInput): ContentRegistry<T> {
  const issues: ContentIssue[] = [];
  const log = input.log === undefined ? (line: string) => console.error(line) : input.log;
  const add = (issue: ContentIssue): void => {
    issues.push(issue);
    log?.(`[content] ${issue.table}${issue.id !== undefined ? `/${issue.id}` : ""}: ${issue.kind}: ${issue.message}`, issue);
  };
  const sink = { refs: [] as FoundRef[] };
  const ajv = makeAjv(sink);

  interface Kept {
    row: { id: string } & Record<string, unknown>;
    refs: FoundRef[];
  }
  const kept = new Map<string, Map<string, Kept>>();
  const info = new Map<string, TableInfo>();

  for (const file of input.tables) {
    const name = isObject(file) && typeof file.table === "string" ? file.table : "?";
    if (!isObject(file) || typeof file.table !== "string" || !ID_PATTERN.test(file.table) || !Number.isInteger(file.version) || file.version < 1 || !Array.isArray(file.rows)) {
      add({ kind: "bad-table", table: name, message: "a table file needs a table name, a whole-number version from 1 and a rows array" });
      continue;
    }
    if (info.has(name)) {
      add({ kind: "bad-table", table: name, message: "two files declare this table; the second is skipped" });
      continue;
    }
    const raw = input.schemas[name];
    if (!raw) {
      add({ kind: "missing-schema", table: name, message: "no schema for this table; every row is skipped" });
      info.set(name, { table: name, version: file.version, rows: 0, skipped: file.rows.length });
      kept.set(name, new Map());
      continue;
    }
    let validate: ValidateFunction;
    try {
      validate = ajv.compile(expandRefs(raw));
    } catch (e) {
      add({ kind: "bad-schema", table: name, message: e instanceof Error ? e.message : String(e) });
      info.set(name, { table: name, version: file.version, rows: 0, skipped: file.rows.length });
      kept.set(name, new Map());
      continue;
    }
    const rows = new Map<string, Kept>();
    const check = input.rowChecks?.[name];
    let skipped = 0;
    for (const [i, row] of file.rows.entries()) {
      const id = isObject(row) && typeof row.id === "string" ? row.id : undefined;
      if (id === undefined || !ID_PATTERN.test(id)) {
        add({ kind: "schema", table: name, path: `/rows/${i}`, message: `row ${i} has no valid string id` });
        skipped++;
        continue;
      }
      if (rows.has(id)) {
        add({ kind: "duplicate-id", table: name, id, message: "a second row with this id is skipped" });
        skipped++;
        continue;
      }
      sink.refs = [];
      if (!validate(row)) {
        add({ kind: "schema", table: name, id, message: formatErrors(validate.errors) });
        skipped++;
        continue;
      }
      const typed = row as { id: string } & Record<string, unknown>;
      const fail = check?.(typed);
      if (fail) {
        add({ kind: "row-check", table: name, id, message: fail });
        skipped++;
        continue;
      }
      rows.set(id, { row: typed, refs: sink.refs });
    }
    kept.set(name, rows);
    info.set(name, { table: name, version: file.version, rows: rows.size, skipped });
  }

  // References: drop rows whose target is missing, then rows whose target was just dropped, until stable.
  for (let changed = true; changed; ) {
    changed = false;
    for (const [table, rows] of kept) {
      for (const [id, k] of rows) {
        const broken = k.refs.find((r) => !kept.get(r.table)?.has(r.id));
        if (!broken) continue;
        const why = !kept.has(broken.table) ? `table "${broken.table}" is not loaded` : `no row "${broken.id}" in "${broken.table}"`;
        add({ kind: "broken-ref", table, id, path: broken.path, message: `reference ${broken.table}/${broken.id} at ${broken.path || "/"}: ${why}; the row is skipped` });
        rows.delete(id);
        const t = info.get(table)!;
        t.rows--;
        t.skipped++;
        changed = true;
      }
    }
  }

  const rowsOf = (table: string): readonly unknown[] => [...(kept.get(table)?.values() ?? [])].map((k) => k.row);
  return {
    issues,
    tables: () => [...info.values()].map((t) => ({ ...t })),
    version: (table) => info.get(table)?.version,
    has: (table, id) => kept.get(table)?.has(id) ?? false,
    get: (table, id) => kept.get(table)?.get(id)?.row as never,
    rows: (table) => rowsOf(table) as never,
    resolve: (ref) => kept.get(ref.table)?.get(ref.id)?.row as never,
  };
}

/** The build-time check: load quietly and return every issue. A build fails when the list is not empty. */
export function validateContent(input: Omit<ContentInput, "log">): ContentIssue[] {
  return [...loadContent({ ...input, log: null }).issues];
}
